import React, { useState, useEffect } from "react";
import { useSeo } from "../seo";
import { Plus, Minus } from "lucide-react";
import { DynamicNavigation, Footer } from "../components";
import CTA02 from "../components/CTA02Section";
import { useNavLinkClick } from "../hooks/useNavLinkClick";

/* ─── Data ─────────────────────────────────────────────────────────────── */

const visaCards = [
  {
    subclass: "Subclass 482",
    title: "Temporary Skill Shortage Visa",
    desc: "The Subclass 482 visa allows Australian employers to sponsor skilled workers from overseas to fill positions where there is a shortage of suitably skilled Australian workers. It enables overseas professionals to temporarily live and work in Australia with an approved sponsoring employer.",
    color: "indigo",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M12 12v4M10 14h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    subclass: "Subclass 186",
    title: "Employer Nomination Scheme",
    desc: "The Employer Nomination Scheme visa allows Australian employers to nominate skilled workers for permanent residence in Australia. This visa is commonly used by businesses that wish to sponsor and retain skilled employees on a long-term basis.",
    highlight: "Direct Permanent Residency",
    color: "violet",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    subclass: "Subclass 494",
    title: "Skilled Employer Sponsored Regional Visa",
    desc: "The Subclass 494 visa enables regional employers to sponsor overseas professionals for skilled roles located in regional areas of Australia. It helps support workforce needs outside major metropolitan regions.",
    color: "blue",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 2C7.589 2 4 5.589 4 10c0 6.222 6.667 11.556 7.432 12.12a1 1 0 0 0 1.136 0C13.333 21.556 20 16.222 20 10c0-4.411-3.589-8-8-8z" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    ),
  },
];

const employerBenefits = [
  "Access qualified global talent",
  "Fill urgent vacancies faster",
  "Solve critical skill shortages",
  "Improve business productivity",
  "Support business expansion",
  "Retain specialist staff long-term",
];

const workerBenefits = [
  "Work legally in Australia",
  "Gain international experience",
  "Competitive salary packages",
  "Career growth opportunities",
  "Potential PR pathways",
  "Include eligible family members",
];

const sponsorSteps = [
  {
    step: "01",
    title: "Business Sponsorship Approval",
    desc: "The employer must first apply to become an approved sponsor (Standard Business Sponsor) under Australian immigration regulations. This confirms that the business is legally established and eligible to sponsor overseas workers.",
    image: "/assets/services-cards/images/employer-sponsored-visas.jpg",
  },
  {
    step: "02",
    title: "Position Nomination",
    desc: "The employer nominates a role that meets the requirements for employer sponsorship, including occupation eligibility, genuine need, and salary thresholds that meet or exceed the Temporary Skilled Migration Income Threshold (TSMIT).",
    image: "/assets/services-cards/images/corporate-immigration.jpg",
  },
  {
    step: "03",
    title: "Visa Application",
    desc: "Once the nomination is approved, the overseas worker can submit their visa application to work in Australia for the sponsoring employer, accompanied by all required documents.",
    image: "/assets/services-cards/images/skills-assessment.jpg",
  },
  {
    step: "04",
    title: "Decision & Employment Start",
    desc: "The Department of Home Affairs reviews the application. If approved, the worker can commence employment with the sponsoring business and begin their life in Australia.",
    image: "/assets/services-cards/images/skilled-visas.jpg",
  },
];

const comparisonData = [
  {
    type: "Temporary Skill Shortage (482)",
    purpose: "Allows employers to sponsor skilled workers when local workers are unavailable",
    duration: "Up to 4 years",
    pr: "Possible PR pathway",
    suitableFor: "Businesses needing skilled professionals for temporary roles",
  },
  {
    type: "Employer Nomination Scheme (186)",
    purpose: "Allows employers to nominate skilled workers for permanent residency",
    duration: "Permanent",
    pr: "Permanent residence visa",
    suitableFor: "Skilled workers nominated for long-term employment",
  },
  {
    type: "Skilled Employer Sponsored Regional (494)",
    purpose: "Allows regional employers to sponsor overseas workers",
    duration: "Up to 5 years",
    pr: "Pathway to PR through Subclass 191",
    suitableFor: "Employers located in regional Australia",
  },
];

const faqItems = [
  {
    q: "What is an employer-sponsored visa?",
    a: "An employer-sponsored visa allows an Australian business to sponsor a skilled overseas worker to fill a role when suitable local candidates are not available. The employer must be an approved sponsor and nominate a genuine position that meets the relevant criteria.",
  },
  {
    q: "Who can sponsor overseas workers?",
    a: "Businesses must first become approved sponsors under Australian immigration regulations before they can sponsor skilled overseas workers. The employer applies to be a Standard Business Sponsor (SBS) through the Department of Home Affairs.",
  },
  {
    q: "Do employer-sponsored visas lead to permanent residency?",
    a: "Some employer-sponsored visas provide pathways to permanent residency. The Employer Nomination Scheme (Subclass 186) offers direct permanent residence, while other visas such as Subclass 494 may lead to PR through additional pathways.",
  },
  {
    q: "Can a sponsored worker change employers?",
    a: "In most cases, the visa holder must continue working for the sponsoring employer unless a new sponsorship arrangement is approved. Subclass 482 holders may transfer to another approved sponsor under certain conditions.",
  },
  {
    q: "How long does the process take?",
    a: "Processing times can vary depending on the visa type, the completeness of the documentation, and government processing timelines. Speaking with a migration specialist can help you understand current timeframes for your situation.",
  },
  {
    q: "What industries use employer-sponsored visas most?",
    a: "Healthcare, construction, engineering, information technology, hospitality, education, logistics, and regional industries regularly use employer-sponsored visa pathways to access skilled international talent.",
  },
];

/* ─── Sub-components ────────────────────────────────────────────────────── */

const VisaCard = ({
  subclass, title, desc, highlight, color, icon,
}: (typeof visaCards)[0]) => {
  const [hovered, setHovered] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const ref = React.useRef<HTMLDivElement>(null);

  const colorMap: Record<string, string> = {
    indigo: "text-indigo-600 bg-indigo-50 border-indigo-100",
    violet: "text-violet-600 bg-violet-50 border-violet-100",
    blue: "text-blue-600 bg-blue-50 border-blue-100",
  };
  const gradientMap: Record<string, string> = {
    indigo: "from-indigo-400 via-blue-500 to-indigo-600",
    violet: "from-violet-400 via-purple-500 to-violet-600",
    blue: "from-blue-400 via-cyan-500 to-blue-600",
  };
  const accentMap: Record<string, string> = {
    indigo: "from-indigo-500 to-blue-500",
    violet: "from-violet-500 to-purple-500",
    blue: "from-blue-500 to-cyan-500",
  };

  return (
    <div
      ref={ref}
      onMouseMove={(e) => {
        if (!ref.current) return;
        const b = ref.current.getBoundingClientRect();
        setPos({ x: e.clientX - b.left, y: e.clientY - b.top });
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative rounded-2xl p-0.5 overflow-hidden bg-white w-full cursor-default"
      style={{ boxShadow: hovered ? "0 8px 40px rgba(99,102,241,0.18)" : "0 2px 12px rgba(0,0,0,0.06)" }}
    >
      {hovered && (
        <div
          className={`pointer-events-none blur-2xl bg-gradient-to-r ${gradientMap[color]} size-56 absolute z-0 opacity-40 transition-opacity duration-300`}
          style={{ top: pos.y - 112, left: pos.x - 112 }}
        />
      )}
      <div className="relative z-10 bg-white rounded-[14px] p-7 h-full w-full flex flex-col">
        {/* top accent bar */}
        <div className={`h-1 w-12 rounded-full bg-gradient-to-r ${accentMap[color]} mb-5`} />
        {/* subclass badge + icon */}
        <div className="flex items-center justify-between mb-4">
          <span className={`inline-flex items-center text-xs font-semibold border rounded-full px-3 py-1 ${colorMap[color]}`}>
            {subclass}
          </span>
          <span className={`${colorMap[color].split(" ")[0]} opacity-60`}>{icon}</span>
        </div>
        <h3 className="text-base font-bold text-gray-800 mb-3 leading-snug">{title}</h3>
        <p className="text-sm text-slate-500 leading-relaxed flex-1">{desc}</p>
        {highlight && (
          <div className={`mt-5 inline-flex items-center gap-1.5 text-xs font-semibold ${colorMap[color].split(" ")[0]} bg-gradient-to-r ${accentMap[color]} bg-clip-text`}
            style={{ WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="shrink-0" style={{ color: "#7c3aed" }}>
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor" />
            </svg>
            {highlight}
          </div>
        )}
      </div>
    </div>
  );
};

const FAQAccordion = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return (
    <div className="space-y-3">
      {faqItems.map((item, i) => (
        <div
          key={i}
          className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
            openIdx === i ? "border-indigo-200 bg-white shadow-lg shadow-indigo-50" : "border-slate-200 bg-slate-50/60"
          }`}
        >
          <button
            className="w-full flex items-center justify-between px-6 py-5 text-left gap-4"
            onClick={() => setOpenIdx(openIdx === i ? null : i)}
            aria-expanded={openIdx === i}
          >
            <span className="text-sm sm:text-base font-semibold text-gray-800 pr-2">{item.q}</span>
            <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
              openIdx === i ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-500"
            }`}>
              {openIdx === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </span>
          </button>
          {openIdx === i && (
            <div className="px-6 pb-6">
              <div className="h-px bg-slate-200 mb-4" />
              <p className="text-sm text-slate-600 leading-relaxed">{item.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ─── Page ──────────────────────────────────────────────────────────────── */

export default function EmployerSponsoredVisasAustralia() {
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState<number | null>(null);
  const [slideDir, setSlideDir] = useState<"next" | "prev">("next");
  const [animating, setAnimating] = useState(false);

  const navigate = (dir: "next" | "prev") => {
    if (animating) return;
    const next =
      dir === "next"
        ? (activeStep + 1) % sponsorSteps.length
        : (activeStep - 1 + sponsorSteps.length) % sponsorSteps.length;
    setSlideDir(dir);
    setPrevStep(activeStep);
    setActiveStep(next);
    setAnimating(true);
    setTimeout(() => { setPrevStep(null); setAnimating(false); }, 540);
  };

  const goTo = (idx: number) => {
    if (idx === activeStep || animating) return;
    setSlideDir(idx > activeStep ? "next" : "prev");
    setPrevStep(activeStep);
    setActiveStep(idx);
    setAnimating(true);
    setTimeout(() => { setPrevStep(null); setAnimating(false); }, 540);
  };

  useEffect(() => {
    const t = setInterval(() => navigate("next"), 3500);
    return () => clearInterval(t);
  }, [activeStep, animating]);

  const links = [
    { id: "home", label: "Home", href: "/" },
    { id: "services", label: "Services", href: "/services" },
    { id: "about", label: "About", href: "/about" },
    { id: "contact", label: "Contact", href: "/contact" },
  ];
  const onLinkClick = useNavLinkClick(links);

  useSeo({
    title: "Employer-Sponsored Visas Australia | 482, 186 & 494 Visa Help | Hola World Migration",
    description:
      "Hire skilled overseas workers in Australia with employer-sponsored visas. Learn about Subclass 482, 186 & 494 visa pathways, eligibility, process, and expert support from Hola World Migration.",
    path: "/employer-sponsored-visas-australia",
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Employer-Sponsored Visas Australia", url: "/employer-sponsored-visas-australia" },
    ],
    faqItems: faqItems.map((f) => ({ question: f.q, answer: f.a })),
  });

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* NAV */}
      <header className="w-full">
        <DynamicNavigation
          links={links}
          logo={<img src="/logo.png" alt="Hola World Migration" className="h-10 w-auto" />}
          showLabelsOnMobile={true}
          glowIntensity={6}
          onLinkClick={onLinkClick}
        />
      </header>

      <main className="w-full flex flex-col items-center max-w-full">

        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <section className="bg-gradient-to-b from-[#F5F7FF] via-[#fffbee] to-[#E6EFFF] w-full">
          <div className="flex flex-col md:flex-row items-center max-md:text-center justify-between mt-24 pb-16 px-6 sm:px-10 md:px-24 max-w-7xl mx-auto w-full gap-8">
            {/* Left */}
            <div className="flex flex-col items-center md:items-start">
              <button
                type="button"
                className="mt-16 mb-6 flex items-center gap-2 border border-indigo-600 text-indigo-600 text-xs rounded-full px-4 py-1.5 hover:bg-indigo-50 transition"
              >
                <span>Employer-Sponsored Visa Pathways · Australia</span>
              </button>
              <h1
                className="text-gray-900 font-semibold text-4xl sm:text-5xl md:text-6xl max-w-xl leading-tight"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Employer-Sponsored Visas{" "}
                <span className="text-indigo-600">Australia</span>
              </h1>
              <p className="mt-4 text-gray-600 max-w-md text-sm sm:text-base leading-relaxed">
                Hire skilled international professionals and strengthen your workforce with the right employer-sponsored visa pathway.
              </p>
              <p className="mt-3 text-gray-500 max-w-md text-sm leading-relaxed">
                Australian businesses facing staff shortages can legally recruit qualified overseas workers through approved employer-sponsored visa programs. Whether you need temporary staff or long-term skilled employees, the right visa pathway can help your business grow.
              </p>

            </div>
            {/* Right: 2×2 image grid */}
            <div aria-label="Employer sponsored visa images" className="mt-12 md:mt-0 grid grid-cols-2 gap-6 pb-6 shrink-0">
              <img alt="Employer Sponsored Visas" src="/assets/services-cards/images/employer-sponsored-visas.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
              <img alt="Corporate Immigration" src="/assets/services-cards/images/corporate-immigration.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
              <img alt="Skilled Visas" src="/assets/services-cards/images/skilled-visas.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
              <img alt="Skills Assessment" src="/assets/services-cards/images/skills-assessment.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
            </div>
          </div>
        </section>

        {/* ── SECTION 1: OVERVIEW ──────────────────────────────────────── */}
        <section className="w-full bg-white py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 lg:gap-16">
            <div className="relative rounded-2xl overflow-hidden shrink-0 w-full max-w-xs sm:max-w-sm md:max-w-md group">
              <img
                loading="lazy"
                className="w-full object-cover rounded-2xl aspect-square transition-transform duration-500 ease-in-out group-hover:scale-110"
                src="/assets/services-cards/images/employer-sponsored-visas.jpg"
                alt="Employer-Sponsored Visas Australia"
              />
            </div>
            <div className="w-full max-w-lg">
              <h2
                className="text-2xl sm:text-3xl md:text-4xl uppercase font-semibold text-slate-700"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Employer-Sponsored Visas
              </h2>
              <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mt-2 mb-6" />
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
                Employer-sponsored visas allow Australian businesses to recruit skilled professionals from overseas when suitable workers cannot be found locally. These visa pathways enable qualified individuals to live and work in Australia while being nominated by an approved Australian employer.
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
                For many organisations facing skill shortages, employer sponsorship provides an effective way to access global talent and support business growth.
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                Businesses across healthcare, construction, hospitality, engineering, IT, trades, education, logistics, and regional industries regularly use these pathways to access international talent.
              </p>
            </div>
          </div>
        </section>

        {/* ── SECTION 2: WHAT IS AN EMPLOYER-SPONSORED VISA? ───────────── */}
        <section className="w-full bg-blue-50 py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              What is an Employer-Sponsored Visa?
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-7" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>
                <p className="text-base text-slate-600 leading-relaxed mb-4">
                  An employer-sponsored visa allows an Australian employer to nominate a skilled overseas worker for a specific role within their organisation. Once the nomination and visa requirements are satisfied, the worker can legally live and work in Australia for the sponsoring employer.
                </p>
                <p className="text-base text-slate-600 leading-relaxed">
                  These visa pathways help businesses fill important skill gaps while giving skilled professionals the opportunity to contribute to the Australian workforce. For many applicants, some employer-sponsored visas can also create a pathway to permanent residency.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  "Fill skill shortages in the Australian labour market",
                  "Support business expansion and growth",
                  "Bring in specialist expertise",
                  "Strengthen regional workforce supply",
                  "Offer skilled workers career opportunities",
                  "Create pathways to permanent residency",
                ].map((label, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 bg-white rounded-xl px-4 py-3.5 border border-slate-100 shadow-sm"
                  >
                    <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center shrink-0">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </div>
                    <p className="text-sm text-slate-700 font-medium leading-snug">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 3: VISA OPTIONS ───────────────────────────────────── */}
        <section className="w-full bg-white py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Common Employer-Sponsored Visa Options
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5" />
            <p className="text-base text-slate-500 mb-10 max-w-3xl">
              Australia offers several employer-sponsored visa subclasses to suit different business needs and employment circumstances.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {visaCards.map((card, i) => (
                <VisaCard key={i} {...card} />
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 4: BENEFITS ───────────────────────────────────────── */}
        <section className="w-full bg-blue-50 py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto flex flex-col md:flex-row gap-14 items-start justify-between">

            {/* Left: heading + highlights */}
            <div className="w-full max-w-xs shrink-0">
              <h2
                className="text-3xl font-semibold text-[#0a2540]"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Benefits of Employer Sponsorship
              </h2>
              <div className="w-20 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mt-3 mb-5" />
              <p className="text-sm leading-relaxed text-slate-500">
                Employer-sponsored visa programs offer real advantages for both businesses and skilled professionals seeking opportunities in Australia.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    label: "Solve skill shortages quickly",
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
                      </svg>
                    ),
                  },
                  {
                    label: "Pathway to permanent residency",
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 22V12h6v10"/>
                      </svg>
                    ),
                  },
                  {
                    label: "Government-approved pathways",
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>
                      </svg>
                    ),
                  },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-3 text-slate-500">
                    <div className="p-2.5 border border-slate-200 rounded-lg shrink-0 text-slate-400">
                      {f.icon}
                    </div>
                    <p className="text-sm font-medium">{f.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: two cards */}
            <div className="flex flex-row items-start justify-center gap-6 w-full">

              {/* Employers card — light */}
              <div className="flex-1 min-w-0 rounded-xl p-6 pb-8 bg-white border border-slate-200 shadow-sm">
                <div className="flex flex-col items-center text-center mb-2">
                  <div className="w-11 h-11 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center mb-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <rect x="2" y="7" width="20" height="14" rx="2" stroke="#4f46e5" strokeWidth="1.8"/>
                      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h3 className="text-base font-bold text-gray-800">For Employers</h3>
                  <p className="text-xs text-slate-400 mt-1">Grow your workforce with global talent</p>
                </div>
                <div className="mt-3 flex flex-col">
                  {employerBenefits.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 border-b border-slate-100 py-3 last:border-0 text-sm text-slate-600">
                      <div className="rounded-full p-1 bg-slate-800 shrink-0">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5"/>
                        </svg>
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Workers card — dark */}
              <div className="flex-1 min-w-0 rounded-xl p-6 pb-8 bg-[#0a2540] text-white shadow-sm">
                <div className="flex flex-col items-center text-center mb-2">
                  <div className="w-11 h-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center mb-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="8" r="4" stroke="white" strokeWidth="1.8"/>
                      <path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h3 className="text-base font-bold">For Skilled Workers</h3>
                  <p className="text-xs text-slate-400 mt-1">Build your career and future in Australia</p>
                </div>
                <div className="mt-3 flex flex-col">
                  {workerBenefits.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 border-b border-white/10 py-3 last:border-0 text-sm text-slate-300">
                      <div className="rounded-full p-1 bg-white/15 shrink-0">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5"/>
                        </svg>
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ── SECTION 5: PROCESS STEPPER ───────────────────────────────── */}
        <section className="w-full relative overflow-hidden" style={{ background: "#1B31F5" }}>
          <div className="max-w-6xl mx-auto px-6 sm:px-10 md:px-16 pt-16 pb-0 relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-end pr-4 sm:pr-20 pointer-events-none select-none z-0 overflow-hidden"
            >
              <span
                key={`num-${activeStep}`}
                className="animate-process-num text-[320px] sm:text-[420px] md:text-[520px] font-black leading-none"
                style={{ color: "rgba(255,255,255,0.13)", letterSpacing: "-0.04em" }}
              >
                {sponsorSteps[activeStep].step}
              </span>
            </div>
            <h2
              className="relative z-10 text-white font-bold text-3xl sm:text-4xl md:text-5xl text-center mb-12"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Steps in the Employer Sponsorship Process
            </h2>
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">
              {/* Left: info */}
              <div
                key={activeStep}
                className={`flex-1 max-w-lg ${prevStep !== null ? (slideDir === "next" ? "proc-step-enter-right" : "proc-step-enter-left") : ""}`}
              >
                <p className="text-white/50 text-xs uppercase tracking-[0.2em] font-semibold mb-3">
                  Step {sponsorSteps[activeStep].step}
                </p>
                <h3 className="text-white font-bold text-3xl sm:text-4xl mb-4 leading-snug">
                  {sponsorSteps[activeStep].title}
                </h3>
                <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-10">
                  {sponsorSteps[activeStep].desc}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate("prev")}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center transition"
                    aria-label="Previous step"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18l-6-6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    onClick={() => navigate("next")}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center transition"
                    aria-label="Next step"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
              {/* Right: image */}
              <div className="flex-shrink-0 flex items-end justify-center lg:justify-end mb-14">
                <div className="relative w-80 h-64 sm:w-[440px] sm:h-80">
                  {prevStep !== null && prevStep < sponsorSteps.length && (
                    <img
                      src={sponsorSteps[prevStep].image}
                      alt=""
                      aria-hidden="true"
                      className={`absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl shadow-black/40 ${slideDir === "next" ? "proc-img-exit-left" : "proc-img-exit-right"}`}
                    />
                  )}
                  <img
                    key={activeStep}
                    src={sponsorSteps[activeStep].image}
                    alt={sponsorSteps[activeStep].title}
                    className={`absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl shadow-black/40 ${prevStep !== null ? (slideDir === "next" ? "proc-img-enter-right" : "proc-img-enter-left") : ""}`}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Timeline dots */}
          <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 md:px-16">
            <div className="flex items-start gap-0 overflow-x-auto pb-12 scrollbar-hide">
              {sponsorSteps.map((s, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`flex flex-col items-start flex-1 min-w-[90px] pr-3 transition-opacity duration-300 ${activeStep === i ? "opacity-100" : "opacity-40 hover:opacity-65"}`}
                >
                  <div className="relative w-full flex items-center mb-4" style={{ height: "14px" }}>
                    <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-white/25" />
                    <span className={`relative z-10 block rounded-full border-2 transition-all duration-300 shrink-0 ${activeStep === i ? "w-3.5 h-3.5 bg-white border-white shadow-[0_0_8px_2px_rgba(255,255,255,0.45)]" : "w-2 h-2 bg-white/30 border-white/40"}`} />
                  </div>
                  <span className={`font-bold text-base sm:text-lg leading-none ${activeStep === i ? "text-white" : "text-white/70"}`}>{s.step}</span>
                  <span className={`text-xs mt-1.5 leading-snug text-left ${activeStep === i ? "text-white font-semibold" : "text-white/55"}`}>{s.title}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 6: COMPARISON TABLE ──────────────────────────────── */}
        <section className="w-full bg-white py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Employer-Sponsored Visa Comparison
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-8" />

            {/* Desktop table */}
            <div className="hidden md:block rounded-2xl overflow-hidden border border-slate-200 shadow-md">
              <table className="w-full text-sm text-left border-collapse">
                <thead>
                  <tr>
                    {[
                      { label: "Visa Type", w: "w-[18%]" },
                      { label: "Purpose", w: "w-[26%]" },
                      { label: "Duration", w: "w-[12%]" },
                      { label: "PR Pathway", w: "w-[18%]" },
                      { label: "Suitable For", w: "w-[26%]" },
                    ].map((h) => (
                      <th
                        key={h.label}
                        className={`${h.w} px-6 py-4 text-xs font-bold uppercase tracking-widest text-slate-400 bg-slate-50 border-b border-slate-200`}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, i) => (
                    <tr
                      key={i}
                      className="border-b border-slate-100 last:border-0 hover:bg-indigo-50/30 transition-colors group"
                    >
                      <td className="px-6 py-5 align-top">
                        <span className="inline-block font-bold text-[#0a2540] text-sm leading-snug">
                          {row.type}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-slate-500 text-sm align-top leading-relaxed">{row.purpose}</td>
                      <td className="px-6 py-5 align-top">
                        <span className={`inline-block text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ${
                          row.duration === "Permanent"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-blue-50 text-blue-700 border border-blue-200"
                        }`}>
                          {row.duration}
                        </span>
                      </td>
                      <td className="px-6 py-5 align-top">
                        <span className={`inline-block text-xs font-semibold px-3 py-1.5 rounded-full ${
                          row.duration === "Permanent"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-50 text-amber-700 border border-amber-200"
                        }`}>
                          {row.pr}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-slate-500 text-sm align-top leading-relaxed">{row.suitableFor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile stacked cards */}
            <div className="md:hidden space-y-4">
              {comparisonData.map((row, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                  <h3 className="text-sm font-bold text-[#0a2540] mb-4">{row.type}</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Purpose</span>
                      <p className="text-slate-600 mt-1 leading-relaxed">{row.purpose}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Duration</span>
                        <p className="mt-1">
                          <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${row.duration === "Permanent" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-blue-50 text-blue-700 border border-blue-200"}`}>
                            {row.duration}
                          </span>
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">PR Pathway</span>
                        <p className="mt-1">
                          <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${row.duration === "Permanent" ? "bg-emerald-100 text-emerald-700" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                            {row.pr}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Suitable For</span>
                      <p className="text-slate-600 mt-1 leading-relaxed">{row.suitableFor}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 7: FAQ ────────────────────────────────────────────── */}
        <section className="w-full bg-sky-50 py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left sticky */}
            <div className="lg:sticky lg:top-24">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 mb-6">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#4f46e5" strokeWidth="2" />
                  <path d="M12 16v-4M12 8h.01" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Common Questions</span>
              </span>
              <h2
                className="text-4xl md:text-5xl font-black mb-6 tracking-tight leading-[1.1] text-gray-900"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Your questions,{" "}
                <br />
                <span className="text-indigo-600 italic underline decoration-indigo-200 underline-offset-8">answered.</span>
              </h2>
              <p className="text-slate-500 text-sm max-w-sm mb-8 leading-relaxed">
                Can't find what you're looking for? Speak with our migration specialists for personalised advice on employer-sponsored visa options.
              </p>
              <img
                src="/assets/faq/faq-image.png"
                alt="FAQ support"
                loading="lazy"
                className="w-full max-w-sm"
              />
            </div>
            {/* Right: accordion */}
            <div>
              <FAQAccordion />
            </div>
          </div>
        </section>

        {/* ── SECTION 8: HOW HOLA WORLD SUPPORTS ───────────────────────── */}
        <section className="w-full bg-blue-50 py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-12">
            <div className="w-full max-w-lg">
              <h2
                className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-4"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                How Hola World Migration Supports Employers
              </h2>
              <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-7" />
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                Employer-sponsored visa applications involve several steps, including sponsorship approval, position nomination, and visa lodgement.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                Hola World Migration provides guidance throughout this process, helping businesses understand available visa options and the documentation required for employer-sponsored visa applications.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                Our team works with clients to organise information and prepare applications in line with Australian immigration requirements.
              </p>
            </div>
            <div className="relative rounded-2xl overflow-hidden shrink-0 w-full max-w-xs sm:max-w-sm group">
              <img
                loading="lazy"
                className="w-full object-cover rounded-2xl aspect-square transition-transform duration-500 ease-in-out group-hover:scale-110"
                src="/assets/services-cards/images/corporate-immigration.jpg"
                alt="Hola World Migration employer support"
              />
            </div>
          </div>
        </section>

        {/* ── DISCLAIMER ───────────────────────────────────────────────── */}
        <div className="py-8 px-6 md:px-24 bg-white border-t border-gray-100 w-full">
          <p className="text-xs text-gray-400 leading-relaxed max-w-5xl mx-auto">
            <strong className="text-gray-500">Disclaimer:</strong> The information on this page is intended as general guidance only and does not constitute legal, migration, or professional advice. Visa requirements, processing times, and occupation lists are subject to change by the Australian Government without notice. Always verify current requirements directly with the Department of Home Affairs or consult a registered migration agent (MARA) before making decisions about your migration pathway.
          </p>
        </div>

        <CTA02 />
      </main>

      <Footer />
    </div>
  );
}
